import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, Check, ShoppingBag } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { db } from '../db/database';

const OrderConfirm = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, station, node } = useAuth();
  const cart = location.state?.cart || [];
  const bcvRateRaw = typeof window !== 'undefined' ? window.localStorage.getItem('bcvRate') : null;
  const bcvRate = bcvRateRaw ? parseFloat(bcvRateRaw) : null;

  const getTotal = () => {
    return cart.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  };

  const totalUsd = getTotal();
  const totalBs = bcvRate && !Number.isNaN(bcvRate) && bcvRate > 0 ? totalUsd * bcvRate : null;

  const handleConfirm = async () => {
    setIsSubmitting(true);
    
    try {
      // Generate unique UUID for the order
      const uuid = crypto.randomUUID();
      
      // Save order with UUID, username for audit, and pending status
      const orderId = await db.orders.add({
        uuid,
        stationId: station.id,
        nodeId: node?.id || null,
        userId: user.id,
        username: user.username,
        createdAt: new Date().toISOString(),
        total: getTotal(),
        status: 'pending',
        uploadStatus: 'pending',
        syncedAt: null
      });

      // Save order items (order details)
      for (const item of cart) {
        await db.orderItems.add({
          orderId,
          productId: item.id,
          quantity: item.quantity,
          price: item.price,
          uploadStatus: 'pending'
        });
      }

      setShowSuccess(true);
      setTimeout(() => {
        navigate('/products');
      }, 2000);
    } catch (error) {
      console.error('Error saving order:', error);
      alert('Error al guardar la orden');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (showSuccess) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center p-4">
        <div className="text-center">
          <div className="w-24 h-24 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-6 animate-pulse">
            <Check size={48} className="text-white" />
          </div>
          <h2 className="text-3xl font-bold text-white mb-2">¡Orden Guardada!</h2>
          <p className="text-gray-400">Redirigiendo...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 pb-32">
      <div className="bg-gradient-to-r from-primary-600 to-secondary-600 p-4 sticky top-0 z-10 shadow-lg">
        <div className="flex items-center gap-4">
          <button onClick={() => navigate(-1)} className="p-2 bg-white/20 rounded-lg">
            <ArrowLeft size={24} className="text-white" />
          </button>
          <div>
            <h1 className="text-xl font-bold text-white">Confirmar Orden</h1>
            <p className="text-sm text-white/80">{node?.name || station?.name}</p>
          </div>
        </div>
      </div>

      <div className="p-4 space-y-4">
        {cart.map((item, index) => (
          <div key={index} className="card p-4 flex justify-between items-center">
            <div>
              <h3 className="text-white font-semibold">{item.name}</h3>
              <p className="text-gray-400 text-sm">Cantidad: {item.quantity}</p>
            </div>
            <p className="text-primary-400 font-bold text-lg">
              ${(item.price * item.quantity).toFixed(2)}
            </p>
          </div>
        ))}

        <div className="card p-6 bg-gradient-to-br from-primary-900/50 to-secondary-900/50">
          <div className="flex justify-between items-center mb-4">
            <span className="text-gray-300 text-lg">Subtotal</span>
            <span className="text-white text-xl font-bold">${totalUsd.toFixed(2)}</span>
          </div>
          <div className="border-t border-gray-700 pt-4">
            <div className="flex flex-col gap-2">
              <div className="flex justify-between items-center">
                <span className="text-white text-2xl font-bold">Total (USD)</span>
                <span className="text-primary-400 text-3xl font-bold">${totalUsd.toFixed(2)}</span>
              </div>
              {totalBs && (
                <div className="flex justify-between items-center text-sm text-gray-300">
                  <span>Equivalente en Bs (tasa BCV {bcvRate})</span>
                  <span className="text-lime-300 font-semibold text-xl">
                    Bs {totalBs.toFixed(2)}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="fixed bottom-0 left-0 right-0 bg-gray-800 border-t border-gray-700 p-4">
        <div className="max-w-2xl mx-auto">
          <button onClick={handleConfirm} disabled={isSubmitting} className="btn-primary w-full flex items-center justify-center gap-2">
            <ShoppingBag size={24} />
            {isSubmitting ? 'Guardando...' : 'Confirmar y Guardar Orden'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default OrderConfirm;
